import React, { useState, useEffect } from "react";
import { connect } from "react-redux";

import { get, isNil } from "lodash";

import {
  Radio,
  Button,
  Dialog,
  RadioGroup,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
} from "@material-ui/core";

import { Alert, ToggleButton, ToggleButtonGroup } from "@material-ui/lab";

import CloseIcon from "@material-ui/icons/Close";
import DeleteIcon from "@material-ui/icons/Delete";

import { loadPageData } from "redux/actions/Request";

import SimpleTable from "views/Components/SimpleTable";
import LicenseRestriction from "views/Components/License";

import { BlumiraAgentActivationBanner } from "views/Components/BlumiraAgent/BlumiraAgentActivationBanner";
import { ActivationConfirmationDialog } from "views/Components/BlumiraAgent/ActivationConfirmationDialog";

import { isOrganizationChildOfNFR } from "utils";

import {
  deviceTableFilters,
  deviceDetailsFields,
  devicesTableColumns,
  sendEmailToSalesTeam,
  setLogShippingFeature,
  getPageActionToDispatch,
} from "./utils";

import { devicesPageStyles } from "./devicesPageStyles";

const DevicesPageView = (props) => {
  const classes = devicesPageStyles();

  const [isSaving, setIsSaving] = useState(false);
  const [displayData, setDisplayData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [deviceBeingViewed, setDeviceBeingViewed] = useState({});
  const [isDeviceIsolated, setIsDeviceIsolated] = useState(false);
  const [isActivatingAgent, setIsActivatingAgent] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("All devices");
  const [wasEmailSuccessful, setWasEmailSuccessful] = useState(false);
  const [hasIsolationChanged, setHasIsolationChanged] = useState(false);
  const [isRemoveDeviceDialogOpen, setIsRemoveDeviceDialogOpen] =
    useState(false);
  const [isDeviceDetailDialogOpen, setIsDeviceDetailDialogOpen] =
    useState(false);
  const [isToggleIsolationDialogOpen, setIsToggleIsolationDialogOpen] =
    useState(false);
  const [
    isActivationConfirmationDialogOpen,
    setIsActivationConfirmationDialogOpen,
  ] = useState(false);

  useEffect(() => {
    if (props.ready) {
      if (props.query && props.query.isIsolated)
        handleApplyFilter({}, "Isolated");
      else setDisplayData(props.devices);
    }
  }, [props.ready, props.devices, props.query]);

  const handleApplyFilter = (event, newSelectedFilter) => {
    // I'm thinking this is where we can filter data based on the preset
    // filters defined in deviceTableFilters in the constants file
    const targetFilter = deviceTableFilters.find(
      (filter) => newSelectedFilter === filter.label
    );

    const filteredDataToDisplay = targetFilter.filterMethod(props.devices);

    setDisplayData(filteredDataToDisplay);

    setSelectedFilter(targetFilter.label);
  };

  const handleCloseDeviceDetailDialog = () => {
    setIsSaving(false);
    setErrorMessage("");
    setHasIsolationChanged(false);
    setIsDeviceDetailDialogOpen(false);
  };

  const handleRemoveDevice = () => {
    deviceBeingViewed
      .delete()
      .then(() => {
        props.reload(true);
        toggleRemoveDeviceDialog();
        handleCloseDeviceDetailDialog();
        setErrorMessage("");
      })
      .catch((e) => {
        setErrorMessage("Oops, we had a problem deleting that device.");
      });
  };

  const handleSaveDeviceDetails = () => {
    setIsSaving(true);
    deviceBeingViewed.set({ isIsolated: isDeviceIsolated });
    deviceBeingViewed
      .update()
      .then(() => {
        handleCloseDeviceDetailDialog();
        setIsSaving(false);
        setErrorMessage("");
      })
      .catch((e) => {
        setIsSaving(false);
        setErrorMessage("Oops, we had a problem saving that device.");
      });
  };

  // begin funcs for context menu in SimpleTable
  // we get access to model through contextMenu arg
  const handleOpenDeviceDetailDialog = ({ model }) => {
    // TODO: sounds like maybe we need to make a call
    // to LC to get the device details here
    setIsSaving(false);

    model.read().then((extendedDeviceModel) => {
      setDeviceBeingViewed(extendedDeviceModel);
      setIsDeviceDetailDialogOpen(true);

      // set isolation to easily toggle with radio
      setIsDeviceIsolated(extendedDeviceModel.isIsolated);
    });
  };

  const toggleRemoveDeviceDialog = (contextMenu = {}) => {
    const { model } = contextMenu;
    // if we have a model, the action is from the
    // context menu and we need a model to delete
    if (model) setDeviceBeingViewed(model);

    setIsRemoveDeviceDialogOpen(!isRemoveDeviceDialogOpen);
  };

  const toggleIsolationConfirmationDialog = (contextMenu = {}) => {
    const { model } = contextMenu;
    if (model) setDeviceBeingViewed(model);

    setIsToggleIsolationDialogOpen(!isToggleIsolationDialogOpen);
  };

  const handleIsolationToggle = () => {
    deviceBeingViewed.set({ isIsolated: !deviceBeingViewed.isIsolated });
    deviceBeingViewed.update();
    toggleIsolationConfirmationDialog();
  };

  const isIsolationDisabled = ({ model = {} }) => {
    return !model.alive;
  };

  const isRemoveDeviceDisabled = () => {
    return !props.hasAgentRemovalPrivileges;
  };

  const handleViewUnresolvedFindings = ({ model }) => {
    const deviceNameForFilter = get(model, ["hostname"], "");

    props.dispatch({
      type: "PAGE",
      payload: {
        orgId: model.orgId,
        toplevel: "reporting",
        secondlevel: "findings",
      },
      query: {
        status: 0,
        deviceName: deviceNameForFilter,
      },
    });
  };

  const handleViewDeviceLogs = ({ model }) => {
    const actionToDispatch = getPageActionToDispatch(model);

    props.dispatch(actionToDispatch);
  };
  // end funcs for context menu in SimpleTable

  const handleIsolationToggleFromRadio = () => {
    setHasIsolationChanged(true);
    setIsDeviceIsolated(!isDeviceIsolated);
  };

  const toggleActivationConfirmationDialog = () => {
    setWasEmailSuccessful(false);
    setIsActivationConfirmationDialogOpen(!isActivationConfirmationDialogOpen);
  };

  const handleActivateDevicesFeature = async () => {
    const { isOrgAChildOrg, isOrgAParentOrg, currentOrg, currentUser } = props;

    try {
      if (isOrgAChildOrg || isOrgAParentOrg) {
        setIsActivatingAgent(true);
        await setLogShippingFeature(currentOrg, currentUser.email);
        setIsActivatingAgent(false);

        // reload page for license feature updates
        window.location.reload();
      } else {
        try {
          await sendEmailToSalesTeam(currentOrg.name);
          setWasEmailSuccessful(true);
        } catch (e) {
          setWasEmailSuccessful(false);
        }
      }
    } catch (e) {
      // if anything goes wrong, reset
      setIsActivatingAgent(false);
    }
  };

  const handleActivateClick = async () => {
    const { isOrgAChildOrg, isOrgAParentOrg } = props;

    // allow MSP NFR admins to freely grant access to
    // this feature, else present confirmation dialog
    if (isOrgAChildOrg || isOrgAParentOrg) {
      handleActivateDevicesFeature();
    } else {
      toggleActivationConfirmationDialog();
    }
  };

  const renderRefineSearchSection = () => (
    <div className={classes.quickFilterContainer}>
      <span className={classes.quickFilterLabel}>Quick Filters</span>
      <ToggleButtonGroup
        exclusive
        value={"Quick filters"}
        onChange={handleApplyFilter}
        aria-label="Quick filters for devices table"
      >
        {deviceTableFilters.map(({ label }, index) => (
          <ToggleButton
            key={`${label}-${index}`}
            value={label}
            aria-label={`Apply ${label} quick filter`}
            className={classes.quickFilterButton}
            selected={label === selectedFilter ? true : false}
          >
            {label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </div>
  );

  // TODO: repurpose for upsale
  const renderDisallow = () => {
    return;
  };

  const renderAllow = (children) => {
    return children;
  };

  const renderBanner = (children) => {
    return children;
  };

  const hideBanner = () => {
    return null;
  };

  return (
    <div>
      <LicenseRestriction
        requires="lcagent.create"
        renderAllow={hideBanner}
        renderDisallow={renderBanner}
      >
        <BlumiraAgentActivationBanner
          isOrgAParentOrg={props.isOrgAParentOrg}
          isOrgAChildOrg={props.isOrgAChildOrg}
          isActivatingAgent={isActivatingAgent}
          onActivateClick={handleActivateClick}
        />
      </LicenseRestriction>
      <LicenseRestriction
        requires="lcagent.create"
        renderAllow={renderAllow}
        renderDisallow={renderDisallow}
      >
        <SimpleTable
          isNorthStar
          data={displayData}
          isFetching={!props.ready}
          columns={devicesTableColumns}
          emptyText={
            <span>
              No agents have shipped logs yet
              {/* 
                                FYI: hiding this link until GA
                                <a 
                                    target={'_blank'}
                                    rel={'noopener noreferrer'}
                                    href={blumiraAgentSetupUrl} 
                                    className={classes.leftMarginLink}
                                >
                                    See Blumira Agent setup guide <OpenInNewIcon/>
                                </a> 
                            */}
            </span>
          }
          filterComponent={renderRefineSearchSection()}
          contextMenuItems={[
            {
              text: "Device details",
              onClick: handleOpenDeviceDetailDialog,
            },
            {
              text: "Toggle host isolation",
              onClick: toggleIsolationConfirmationDialog,
              isDisabled: isIsolationDisabled,
              disabledTooltip:
                "Host isolation is not available for offline devices.",
            },
            {
              text: "View unresolved findings",
              onClick: handleViewUnresolvedFindings,
            },
            {
              text: "View device logs",
              onClick: handleViewDeviceLogs,
            },
            {
              text: "Remove device",
              onClick: toggleRemoveDeviceDialog,
              isDisabled: isRemoveDeviceDisabled,
              disabledTooltip:
                "You do not have sufficient permissions to remove this device.",
            },
          ]}
        />
      </LicenseRestriction>
      <Dialog
        open={isDeviceDetailDialogOpen}
        onClose={handleCloseDeviceDetailDialog}
        fullWidth={true}
      >
        <div className={classes.dialogTitle}>
          <DialogTitle>Device details</DialogTitle>
          <CloseIcon
            className={classes.close}
            onClick={handleCloseDeviceDetailDialog}
          />
        </div>
        <DialogContent>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {deviceDetailsFields.map((deviceField, index) => (
            <div
              key={`${index}-${deviceField.title}`}
              className={classes.deviceDetailOuterContainer}
            >
              <div className={classes.deviceDetailInnerContainer}>
                <p className={classes.displayNameText}>{deviceField.title}</p>
              </div>
              <div className={classes.deviceDetailInnerContainer}>
                {deviceField.renderDetailText ? ( // renderDetailText teakes precendence in the dialog
                  deviceField.renderDetailText(deviceField, deviceBeingViewed)
                ) : deviceField.renderValue ? (
                  deviceField.renderValue(deviceField, deviceBeingViewed)
                ) : (
                  <p className={classes.valueText}>
                    {deviceBeingViewed && deviceBeingViewed[deviceField.field]}
                  </p>
                )}
              </div>
            </div>
          ))}
          <div className={classes.hostIsolationContainer}>
            <p className={classes.displayNameText}>Host isolation</p>
            {isIsolationDisabled({ model: deviceBeingViewed }) && (
              <p className={classes.warning}>
                Isolation toggling is currently disabled as your device is
                offline.
              </p>
            )}
            <RadioGroup>
              <FormControlLabel
                value={false}
                label={"Not isolated"}
                className={classes.formControlLabel}
                onChange={handleIsolationToggleFromRadio}
                control={<Radio checked={!isDeviceIsolated} />}
                disabled={isIsolationDisabled({ model: deviceBeingViewed })}
              />
              <FormControlLabel
                value={true}
                className={classes.formControlLabel}
                onChange={handleIsolationToggleFromRadio}
                control={<Radio checked={isDeviceIsolated} />}
                disabled={isIsolationDisabled({ model: deviceBeingViewed })}
                label={
                  "Isolated (Block outgoing network traffic except to Blumira)"
                }
              />
            </RadioGroup>
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            variant={"text"}
            onClick={toggleRemoveDeviceDialog}
            className={classes.errorTextButton}
            disabled={!props.hasAgentRemovalPrivileges}
          >
            <DeleteIcon /> Remove device
          </Button>
          <div>
            <Button
              variant={"text"}
              onClick={handleCloseDeviceDetailDialog}
              color={"primary"}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveDeviceDetails}
              variant={"contained"}
              color={"primary"}
              disabled={!hasIsolationChanged}
            >
              {isSaving ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isRemoveDeviceDialogOpen}
        onClose={toggleRemoveDeviceDialog}
      >
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          You are about to remove this device. This device's agent will be
          un-enrolled and no longer ship logs to Blumira. Existing logs will be
          retained in Blumira.
        </DialogContent>
        <DialogActions>
          <Button
            variant={"text"}
            onClick={toggleRemoveDeviceDialog}
            color={"primary"}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemoveDevice}
            variant={"contained"}
            className={classes.errorContainedButton}
            disabled={!props.hasAgentRemovalPrivileges}
          >
            Remove this device
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isToggleIsolationDialogOpen}
        onClose={toggleIsolationConfirmationDialog}
      >
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          You are about to{" "}
          {deviceBeingViewed.isIsolated ? "release" : "isolate"} this device.
        </DialogContent>
        <DialogActions>
          <Button
            variant={"text"}
            onClick={toggleIsolationConfirmationDialog}
            color={"primary"}
          >
            Cancel
          </Button>
          <Button
            onClick={handleIsolationToggle}
            variant={"contained"}
            className={classes.errorContainedButton}
          >
            {deviceBeingViewed.isIsolated ? "Release" : "Isolate"} this device
          </Button>
        </DialogActions>
      </Dialog>
      <ActivationConfirmationDialog
        wasEmailSuccessful={wasEmailSuccessful}
        open={isActivationConfirmationDialogOpen}
        onClose={toggleActivationConfirmationDialog}
        handleActivateDevicesFeature={handleActivateDevicesFeature}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  const query = get(state, ["location", "query"], {});
  const currentUser = state.session.settings.user;
  const currentOrgId = state.location.payload.orgId;

  // get roles of current user to determine if they are
  // *only* a responder, which restricts deleting
  const { orgRoles } = currentUser;

  // we only need roles set on the current org
  const currentUserOrgRoles = orgRoles.filter(
    ({ orgId }) => orgId === currentOrgId
  );

  // after getting current org roles, find any allowed role
  // for allowing access to restricted features (deleting agent)
  const hasAgentRemovalPrivileges =
    currentUser.superadmin ||
    currentUserOrgRoles.some(
      ({ roleId }) =>
        !![10, 20, 30].find((allowedRole) => allowedRole === roleId)
    );

  const currentOrgJSON = state.session.settings.userOrgs.find(
    ({ id }) => id === currentOrgId
  );

  const isOrgAChildOrg = isOrganizationChildOfNFR(currentOrgJSON);
  const isOrgAParentOrg =
    currentOrgJSON &&
    currentOrgJSON.market === 20 &&
    isNil(currentOrgJSON.parentId);

  return {
    query,
    currentUser,
    isOrgAChildOrg,
    isOrgAParentOrg,
    currentOrg: currentOrgJSON,
    hasAgentRemovalPrivileges: !!hasAgentRemovalPrivileges, // bool to determine if user has permission to remove devices
  };
};

const mapDispatchToProps = (dispatch) => ({
  reload: (force) => dispatch(loadPageData(force)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DevicesPageView);
